/**
 * PATH: packages/ui/src/components/settings/security-tab/TwoFactorSection.tsx
 * PURPOSE: State management and orchestration for the 2FA setup/disable/backup flow.
 * WHY: Extracted from SecurityTab to keep each file under 300 lines.
 *
 * FLOW:
 *   ┌───────────┐    ┌──────────┐    ┌──────────────┐
 *   │ idle/qr   │───▶│ verify   │───▶│ backup/done  │
 *   └───────────┘    └──────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - ./TwoFactorSteps: presentational step sub-components
 *   - lucide-react: header icons only
 *   - ../../lib/utils (cn): classname merge
 */

import { useState, useCallback, useEffect } from 'react'
import { ShieldCheck, ShieldAlert } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { TwoFactorSectionProps } from './security-tab-types'
import {
  SetupPrompt,
  QrCodeStep,
  BackupCodesDisplay,
  EnabledStatus,
  DisableConfirmation,
} from './TwoFactorSteps'

export function TwoFactorSection({
  user,
  enable2FA,
  confirm2FA,
  disable2FA,
  regenerateBackupCodes,
  get2FAStatus,
}: TwoFactorSectionProps) {
  const [is2FAEnabled, setIs2FAEnabled] = useState(user?.totp_enabled || false)
  const [setupStep, setSetupStep] = useState<'idle' | 'qr' | 'verify' | 'backup' | 'disable'>('idle')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showSecret, setShowSecret] = useState(false)
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false)

  useEffect(() => {
    let isMounted = true
    get2FAStatus()
      .then((status) => {
        if (!isMounted || !status) return
        setIs2FAEnabled(!!status.enabled)
        setBackupCodesRemaining(status.backup_codes_remaining || 0)
      })
      .catch(() => { if (!isMounted) return })
    return () => { isMounted = false }
  }, [get2FAStatus])

  const handleStartSetup = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const result = await enable2FA()
      setQrCode(result.qr_code)
      setSecret(result.secret)
      setSetupStep('qr')
    } catch (err: any) {
      setError(err.message || 'Failed to start 2FA setup')
    } finally {
      setIsLoading(false)
    }
  }, [enable2FA])

  const handleConfirm = useCallback(async () => {
    if (!verificationCode) return
    setIsLoading(true)
    setError('')
    try {
      const result = await confirm2FA(verificationCode)
      if (result.success && result.backup_codes) {
        setBackupCodes(result.backup_codes)
        setSetupStep('backup')
        setIs2FAEnabled(true)
      } else {
        setError(result.error || 'Verification failed')
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed')
    } finally {
      setIsLoading(false)
      setVerificationCode('')
    }
  }, [confirm2FA, verificationCode])

  const handleDisable = useCallback(async () => {
    if (!verificationCode) return
    setIsLoading(true)
    setError('')
    try {
      const result = await disable2FA(verificationCode)
      if (result.success) {
        setIs2FAEnabled(false)
        setSetupStep('idle')
        setBackupCodes([])
      } else {
        setError(result.error || 'Failed to disable 2FA')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to disable 2FA')
    } finally {
      setIsLoading(false)
      setVerificationCode('')
    }
  }, [disable2FA, verificationCode])

  const handleRegenerateBackupCodes = useCallback(async () => {
    if (!verificationCode) return
    setIsLoading(true)
    setError('')
    try {
      const result = await regenerateBackupCodes(verificationCode)
      if (result.success && result.backup_codes) {
        setBackupCodes(result.backup_codes)
        setBackupCodesRemaining(result.backup_codes.length)
      } else {
        setError(result.error || 'Failed to regenerate codes')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate codes')
    } finally {
      setIsLoading(false)
      setVerificationCode('')
    }
  }, [regenerateBackupCodes, verificationCode])

  const handleCopyBackupCodes = useCallback(() => {
    navigator.clipboard.writeText(backupCodes.join('\n'))
    setCopiedBackupCodes(true)
    setTimeout(() => setCopiedBackupCodes(false), 2000)
  }, [backupCodes])

  const handleFinishSetup = useCallback(() => {
    setSetupStep('idle')
    setQrCode('')
    setSecret('')
    setVerificationCode('')
    setBackupCodes([])
  }, [])

  const setCode = useCallback((v: string) => setVerificationCode(v), [])

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className={cn('p-2 rounded-lg', is2FAEnabled ? 'bg-green-500/10' : 'bg-amber-500/10')}>
          {is2FAEnabled ? <ShieldCheck className="w-5 h-5 text-green-500" /> : <ShieldAlert className="w-5 h-5 text-amber-500" />}
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Two-Factor Authentication</h3>
          <p className="text-sm text-muted-foreground">
            {is2FAEnabled ? 'Your account is protected with 2FA' : 'Add an extra layer of security'}
          </p>
        </div>
      </div>

      {setupStep === 'idle' && !is2FAEnabled && (
        <SetupPrompt isLoading={isLoading} onStart={handleStartSetup} />
      )}

      {setupStep === 'qr' && (
        <QrCodeStep
          qrCode={qrCode}
          secret={secret}
          showSecret={showSecret}
          onToggleSecret={() => setShowSecret(!showSecret)}
          verificationCode={verificationCode}
          onCodeChange={setCode}
          onConfirm={handleConfirm}
          isLoading={isLoading}
          error={error}
          onCancel={() => setSetupStep('idle')}
        />
      )}

      {setupStep === 'backup' && backupCodes.length > 0 && (
        <BackupCodesDisplay
          backupCodes={backupCodes}
          copied={copiedBackupCodes}
          onCopy={handleCopyBackupCodes}
          onFinish={handleFinishSetup}
        />
      )}

      {setupStep === 'idle' && is2FAEnabled && (
        <EnabledStatus
          backupCodesRemaining={backupCodesRemaining}
          onDisable={() => setSetupStep('disable')}
          onNewBackupCodes={() => { setSetupStep('backup'); setBackupCodes([]) }}
        />
      )}

      {setupStep === 'disable' && (
        <DisableConfirmation
          verificationCode={verificationCode}
          onCodeChange={setCode}
          onDisable={handleDisable}
          isLoading={isLoading}
          error={error}
          onCancel={() => { setSetupStep('idle'); setVerificationCode(''); setError('') }}
        />
      )}
    </section>
  )
}
